import axios from "axios";
import { ProviderUser } from "../types/ProviderUser";
import { getApiBaseUrl } from "../../config/api";
import { GoogleAuth2Service } from "../../infrastructure/oauth2/GoogleOauth2Service";
import { GoogleCalendarEventResponse } from "../types/GoogleCalendarEventResponse";
import { updateProviderUser } from "../../infrastructure/repositories/providerUserRepository";

/**
 * Represents a calendar event to be added to Google Calendar.
 */
interface CalendarEvent {
  summary: string;
  description?: string;
  start: { date: string };
  end: { date: string };
  recurrence?: string[];
}

/**
 * A service for managing interactions with Google Calendar, including authentication,
 * adding events, and disconnecting the calendar.
 */
export class GoogleCalendarService {
  private static instance: GoogleCalendarService;
  private googleAuthService: GoogleAuth2Service =
    GoogleAuth2Service.getInstance();

  private constructor() {
    // Private constructor for singleton pattern
  }

  /**
   * Gets the singleton instance of the GoogleCalendarService.
   * @returns The singleton instance of GoogleCalendarService.
   */
  public static getInstance(): GoogleCalendarService {
    if (!GoogleCalendarService.instance) {
      GoogleCalendarService.instance = new GoogleCalendarService();
    }
    return GoogleCalendarService.instance;
  }

  /**
   * Checks if the Google Calendar is connected by verifying the presence of a refresh token.
   * @param providerUser - The provider user object containing user details and tokens.
   * @returns True if the calendar is connected; otherwise, false.
   */
  public isCalendarConnected(providerUser: ProviderUser): boolean {
    return providerUser.services?.calendar === true;
  }

  /**
   * Connects the user's Google Calendar by initiating the OAuth flow.
   * It uses the GoogleAuth2Service to handle the authorization process.
   * The authorization code is stored, and the backend exchanges it for tokens.
   *
   * @param providerUser - The provider user object containing user details.
   * @returns A promise that resolves when the calendar is connected.
   */
  public async connectCalendar(providerUser: ProviderUser): Promise<void> {
    const BASE_SCOPES = [
      "https://www.googleapis.com/auth/userinfo.email",
      "https://www.googleapis.com/auth/userinfo.profile",
      "openid"
    ];
    const CALENDAR_REQUIRED_SCOPES = [
      "https://www.googleapis.com/auth/calendar.events"
    ];
    const scopes = Array.from(new Set([
      ...BASE_SCOPES,
      ...CALENDAR_REQUIRED_SCOPES
    ]));
    const options = {
      uxMode: "popup" as const,
      loginHint: providerUser.email || "",
      selectAccount: false,
      accessType: "offline",
      prompt: "consent",
    };
    const handleResponse = async (response: { code: string }) => {
      const googleAuthCode = response.code;
      // Batch all updates in a single call
      await updateProviderUser(providerUser.uid, {
        googleAuthCode,
        services: { ...providerUser.services, calendar: true },
        scopes: { ...(providerUser.scopes || {}), calendar: scopes }
      });
    };
    await this.googleAuthService.configureAuthClient(
      scopes,
      handleResponse,
      process.env.REACT_APP_GOOGLE_APP_CLIENT_ID || "",
      options,
    );
    this.googleAuthService.requestCode();
  }

  /**
   * Adds an event to the user's Google Calendar by sending a request to the backend API.
   *
   * @param event - The calendar event to add.
   * @param appUserId - The UID of the user.
   * @param accessToken - The accessToken of the user.
   * @returns A promise that resolves to true if the event was added successfully; otherwise, false.
   */
  public async addEventToCalendar(
    event: CalendarEvent,
    appUserId: string,
    accessToken?: string,
  ): Promise<AddToCalendarResponse> {
    try {
      // Send the event data to the backend API
      const response = await axios.post(
        `${getApiBaseUrl()}/api/v1/calendars/${appUserId}/events`,
        event,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (response.data.success) {
        const returnedEvent: GoogleCalendarEventResponse = response.data.event;
        return { success: true, event: returnedEvent };
      } else {
        return { success: false };
      }
    } catch (error) {
      console.error("Error adding event to Google Calendar:", error);
      return { success: false };
    }
  }

  /**
   * Deletes an event from the user's Google Calendar by sending a request to the backend API.
   *
   * @param appUserId - The UID of the user.
   * @param eventId - The calendar eventId to delete.
   * @param accessToken - The accessToken of the user.
   * @returns A promise that resolves to true if the event was added successfully; otherwise, false.
   */
  async deleteEvent(appUserId: string, eventId: string, accessToken: string) {
    const response = await axios.delete(
      `${getApiBaseUrl()}/api/v1/calendars/${appUserId}/events/${eventId}`,
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      },
    );

    if (response.status !== 204) {
      throw new Error("Failed to delete calendar event");
    }
  }

  /**
   * Disconnects the user's Google Calendar by informing the backend to revoke tokens.
   *
   * @param providerUser - The provider user object containing user details and tokens.
   * @param onSuccess - Optional callback function to execute on successful disconnection.
   * @param onError - Optional callback function to execute on error during disconnection.
   * @returns A promise that resolves when the calendar is disconnected.
   */
  public async disconnectCalendar(
    providerUser: ProviderUser,
    onSuccess?: () => void,
    onError?: (error: Error) => void
  ): Promise<void> {
    try {
      // Remove calendar from services and scopes
      const updatedServices = { ...providerUser.services, calendar: false };
      const updatedScopes = { ...(providerUser.scopes || {}) };
      delete updatedScopes.calendar;
      // Only clear tokens if no Google service is active
      const anyGoogleServiceActive = Object.entries(updatedServices)
        .filter(([key]) => key === "gmail" || key === "calendar")
        .some(([, active]) => active);
      const update: any = {
        services: updatedServices,
        scopes: updatedScopes,
      };
      if (!anyGoogleServiceActive) {
        update.tokens = null;
      }
      await updateProviderUser(providerUser.uid, update);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: unknown) {
      console.error("Error disconnecting Calendar:", error);
      if (onError) {
        onError(error instanceof Error ? error : new Error("Failed to disconnect Calendar"));
      }
    }
  }
}

interface AddToCalendarResponse {
  success: boolean;
  event?: GoogleCalendarEventResponse;
}
